import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Search, Sparkles, BookOpen, MessageSquare, Lightbulb, ArrowRight, X, ShoppingBag,
} from "lucide-react";
import Footer from "@/components/Footer";

const DIFFICULTY_PILL = {
  Easy: "bg-emerald-100 text-emerald-800 border-emerald-200",
  Medium: "bg-amber-100 text-amber-800 border-amber-200",
  Hard: "bg-rose-100 text-rose-800 border-rose-200",
};

export default function InterviewPrep() {
  const [items, setItems] = useState([]);
  const [facets, setFacets] = useState({ categories: [], industries: [], difficulties: [] });
  const [category, setCategory] = useState("all");
  const [industry, setIndustry] = useState("all");
  const [difficulty, setDifficulty] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null); // slug being expanded

  const fetchLib = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (category && category !== "all") params.set("category", category);
      if (industry && industry !== "all") params.set("industry", industry);
      if (difficulty && difficulty !== "all") params.set("difficulty", difficulty);
      if (search.trim()) params.set("search", search.trim());
      const { data } = await api.get(`/interview/prep/library?${params.toString()}`);
      setItems(data.items || []);
      setFacets(data.facets || { categories: [], industries: [], difficulties: [] });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLib(); /* eslint-disable-next-line */ }, [category, industry, difficulty]);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(fetchLib, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search]);

  const grouped = useMemo(() => {
    const out = {};
    items.forEach((q) => {
      const k = q.category || "Other";
      if (!out[k]) out[k] = [];
      out[k].push(q);
    });
    return out;
  }, [items]);

  return (
    <main className="min-h-screen bg-gradient-to-br from-blue-50/40 via-fuchsia-50/30 to-amber-50/40" data-testid="interview-prep-page">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 lg:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-10">
          <div className="lg:col-span-8">
            <div className="inline-flex items-center gap-2 mb-3">
              <span className="inline-flex items-center justify-center w-6 h-6 bg-[#DC1C22] text-white">
                <BookOpen className="w-3.5 h-3.5" strokeWidth={2.25} />
              </span>
              <span className="font-mono uppercase tracking-wider text-[11px] text-zinc-500">Interview Prep Library</span>
            </div>
            <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02] text-zinc-900">
              Practice the questions<br />
              <span className="text-[#DC1C22]">hiring managers actually ask.</span>
            </h1>
            <p className="mt-5 text-zinc-600 max-w-2xl">
              Browse a curated bank of behavioral, technical, situational, and culture-fit questions —
              each with a model-answer outline and 2-3 tactical tips. Free for everyone.
            </p>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end gap-3">
            <Link
              to="/interview/packs"
              data-testid="prep-to-packs-cta"
              className="inline-flex items-center gap-2 px-4 py-3 border border-zinc-900 text-zinc-900 text-sm font-medium hover:bg-zinc-900 hover:text-white transition-colors"
            >
              <ShoppingBag className="w-4 h-4" /> Booster Packs
            </Link>
            <Link
              to="/interview/mock"
              data-testid="prep-to-mock-cta"
              className="inline-flex items-center gap-2 px-5 py-3 bg-zinc-900 text-white text-sm font-medium hover:bg-zinc-800 transition-colors"
            >
              <Sparkles className="w-4 h-4" /> Run a live mock <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white border border-zinc-200 p-5 lg:p-6 mb-8" data-testid="prep-filters">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="relative col-span-1 sm:col-span-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search questions..."
                data-testid="prep-search-input"
                className="w-full pl-9 pr-3 py-2.5 border border-zinc-200 text-sm focus:outline-none focus:border-zinc-900"
              />
            </div>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              data-testid="prep-filter-category"
              className="px-3 py-2.5 border border-zinc-200 text-sm focus:outline-none focus:border-zinc-900 bg-white"
            >
              <option value="all">All categories</option>
              {facets.categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
              data-testid="prep-filter-industry"
              className="px-3 py-2.5 border border-zinc-200 text-sm focus:outline-none focus:border-zinc-900 bg-white"
            >
              <option value="all">All industries</option>
              {facets.industries.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {["all", "Easy", "Medium", "Hard"].map((d) => (
              <button
                key={d}
                onClick={() => setDifficulty(d)}
                data-testid={`prep-filter-difficulty-${d.toLowerCase()}`}
                className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider border transition-colors ${
                  difficulty === d
                    ? "bg-zinc-900 text-white border-zinc-900"
                    : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-900"
                }`}
              >
                {d === "all" ? "All difficulty" : d}
              </button>
            ))}
            <span className="ml-auto text-xs text-zinc-500 font-mono">
              {loading ? "Loading…" : `${items.length} question${items.length === 1 ? "" : "s"}`}
            </span>
          </div>
        </div>

        {/* Question groups */}
        {loading && items.length === 0 ? (
          <div className="text-center py-20 text-zinc-500">Loading library…</div>
        ) : items.length === 0 ? (
          <div className="text-center py-20 text-zinc-500" data-testid="prep-empty-state">
            No questions match. Try clearing filters.
          </div>
        ) : (
          <div className="space-y-10">
            {Object.entries(grouped).map(([cat, list]) => (
              <section key={cat} data-testid={`prep-group-${cat.toLowerCase()}`}>
                <h2 className="font-display font-bold text-2xl text-zinc-900 mb-4">
                  {cat} <span className="text-zinc-400 font-mono text-sm font-normal ml-2">{list.length}</span>
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {list.map((q) => (
                    <article
                      key={q.slug}
                      data-testid={`prep-card-${q.slug}`}
                      className="bg-white border border-zinc-200 p-5 hover:border-zinc-400 transition-colors cursor-pointer"
                      onClick={() => setOpen(q)}
                    >
                      <div className="flex items-start gap-2 mb-3">
                        <span className={`px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider border ${DIFFICULTY_PILL[q.difficulty] || "bg-zinc-100 text-zinc-700"}`}>
                          {q.difficulty || "Medium"}
                        </span>
                        <span className="px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider bg-zinc-100 text-zinc-700 border border-zinc-200">
                          {q.industry}
                        </span>
                      </div>
                      <h3 className="font-display font-semibold text-zinc-900 leading-snug mb-2 text-base">
                        {q.question}
                      </h3>
                      <p className="text-xs text-zinc-500 line-clamp-2">{q.model_answer}</p>
                      <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-[#DC1C22] font-medium">
                        Open answer <ArrowRight className="w-3 h-3" />
                      </div>
                    </article>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>

      {/* Question detail drawer */}
      {open && (
        <div
          className="fixed inset-0 z-50 bg-zinc-900/40 backdrop-blur-sm flex items-end sm:items-center sm:justify-center p-0 sm:p-6"
          role="dialog"
          aria-modal="true"
          data-testid="prep-question-drawer"
          onClick={() => setOpen(null)}
        >
          <div
            className="bg-white w-full sm:max-w-2xl max-h-[90vh] overflow-y-auto border border-zinc-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-zinc-200 px-6 py-4 flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-1.5">
                  <span className={`px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider border ${DIFFICULTY_PILL[open.difficulty] || "bg-zinc-100 text-zinc-700"}`}>
                    {open.difficulty}
                  </span>
                  <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">{open.category} · {open.industry}</span>
                </div>
                <h2 className="font-display font-extrabold text-xl text-zinc-900 leading-tight">
                  {open.question}
                </h2>
              </div>
              <button
                onClick={() => setOpen(null)}
                data-testid="prep-drawer-close"
                className="text-zinc-500 hover:text-zinc-900 p-1"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-6 space-y-6">
              <div>
                <h3 className="font-mono uppercase tracking-wider text-[11px] text-zinc-500 mb-2 flex items-center gap-1.5">
                  <MessageSquare className="w-3.5 h-3.5" /> Model answer outline
                </h3>
                <p className="text-sm text-zinc-700 leading-relaxed">{open.model_answer}</p>
              </div>
              <div>
                <h3 className="font-mono uppercase tracking-wider text-[11px] text-zinc-500 mb-2 flex items-center gap-1.5">
                  <Lightbulb className="w-3.5 h-3.5" /> Tactical tips
                </h3>
                <ul className="space-y-2">
                  {(open.tips || []).map((t, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-zinc-700">
                      <span className="text-[#DC1C22] font-mono text-xs mt-0.5 shrink-0">{i + 1}.</span>
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="pt-4 border-t border-zinc-200">
                <Link
                  to={`/interview/mock?role=${encodeURIComponent(open.industry === "Generic" ? "" : open.industry)}`}
                  data-testid="prep-drawer-mock-cta"
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-zinc-900 text-white text-sm font-medium hover:bg-zinc-800 transition-colors"
                >
                  <Sparkles className="w-4 h-4" /> Practice this in a live mock
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </main>
  );
}
